<template>
  <div id="wrapper">
    <DebugToggle />
    <header class="header" :class="{ 'header--not-logged-in': !isLoggedIn }">
      <div class="container">
        <div class="u-display--flex u-flex-align--center">
          <div class="navbar-brand">
            <router-link
              class="navbar-item logo mr-4"
              :to="{ name: 'landingPage' }"
            >
              &nbsp;
            </router-link>
          </div>

          <MetaMenu ref="metaMenu" @toggled="onMetaMenuToggled"></MetaMenu>
        </div>
        <nav
          class="navbar"
          role="navigation"
          aria-label="main navigation"
          v-if="isLoggedIn"
        >
          <div id="l4bNavBar" class="navbar-menu">
            <div class="navbar-start">
              <a class="navbar-item navbar-item--underline-hover" href="/">
                <i class="fa-solid fa-bed is-white pr-2"></i>
                {{ $t("nav.hotels") }}
              </a>

              <a
                v-if="enableMeetingRequests"
                class="navbar-item navbar-item--underline-hover"
                href="/meetingroomreservation"
              >
                <i class="fa-solid fa-comment is-white pr-2"></i>
                {{ $t("nav.meetingReservation") }}
              </a>
              <a
                v-if="enableFlightRequests || !isProd()"
                class="navbar-item navbar-item--underline-hover"
                href="/flightform"
              >
                <i class="fa-solid fa-plane is-white pr-2"></i>
                {{ $t("nav.flightForm") }}
              </a>
              <a
                v-if="enableTrainRequests || !isProd()"
                class="navbar-item navbar-item--underline-hover"
                href="/trainform"
              >
                <i class="fa-solid fa-train is-white pr-2"></i>
                {{ $t("nav.trainform") }}
              </a>
              <a
                v-if="enableCarRequests || !isProd()"
                class="navbar-item navbar-item--underline-hover"
                href="/carform"
              >
                <i class="fa-solid fa-car is-white pr-2"></i>
                {{ $t("nav.carform") }}
              </a>
              <a
                class="navbar-item navbar-item--underline-hover"
                href="/reservation-overview"
              >
                <i class="fa-solid fa-book is-white pr-2"></i>
                {{ $t("nav.bookingOverview") }}
              </a>
              <div
                class="navbar-item navbar-dropdown-custom header-display--mobile-block"
              >
                <span
                  class="navbar-dropdown-trigger"
                  :class="!isLoggedIn ? 'u-text-color-white' : ''"
                >
                  {{ $t("nav.contact") }}
                </span>
                <div
                  class="navbar-dropdown-content navbar-dropdown-content--inline pt-3 pl-4"
                >
                  <p class="email">
                    ✉️
                    <a href="mailto:zakelijk@lookingforbooking.com"
                      >zakelijk@lookingforbooking.com</a
                    >
                  </p>
                  <hr />
                  <p>
                    📞 {{ $t("nav.contactPhone") }}
                    <strong
                      ><a href="tel:+31503965050"> +31 (0)50-3695050</a></strong
                    >
                  </p>
                </div>
              </div>
              <div
                class="navbar-item navbar-dropdown-custom header-display--mobile-block"
              >
                <span class="navbar-dropdown-trigger">
                  <img
                    :src="
                      $i18n.locale == 'en'
                        ? require('@/assets/images/icons/gb.png')
                        : require('@/assets/images/icons/nl.png')
                    "
                    width="20"
                    class="mr-2"
                  />
                  {{ $t("nav.language") }}
                </span>
                <div
                  class="navbar-dropdown-content navbar-dropdown-content--inline pt-3"
                >
                  <a
                    class="dropdown-item pl-4 pr-4"
                    @click="setLocale('en')"
                    :class="{ 'is-active': $i18n.locale == 'en' }"
                  >
                    <img src="@/assets/images/icons/gb.png" width="20" />
                    English
                  </a>
                  <a
                    :class="{ 'is-active': $i18n.locale == 'nl' }"
                    class="dropdown-item pl-4 pr-4"
                    @click="setLocale('nl')"
                  >
                    <img src="@/assets/images/icons/nl.png" width="20" />
                    Nederlands
                  </a>
                </div>
              </div>
              <div
                class="navbar-item navbar-dropdown-custom header-display--mobile-block"
              >
                <span class="navbar-dropdown-trigger">
                  {{ fullName ?? userName }}
                </span>
                <div
                  class="navbar-dropdown-content navbar-dropdown-content--inline pt-3"
                >
                  <a
                    href="#"
                    @click="refreshCompanyInfo()"
                    class="dropdown-item"
                  >
                    Ververs data
                  </a>
                  <a
                    href="/userprofile"
                    class="dropdown-item"
                    v-if="enableFlightRequests || !isProd()"
                  >
                    {{ $t("profile.metamenuLink") }}
                  </a>
                  <hr class="dropdown-divider" v-if="managementPortalUrl" />
                  <a
                    v-if="managementPortalUrl"
                    :href="managementPortalUrl"
                    target="_blank"
                    class="dropdown-item"
                  >
                    Managementportal
                  </a>

                  <hr class="dropdown-divider" />

                  <a href="#" @click="showModal = true" class="dropdown-item"
                    >{{ $t("loginStatus.logout") }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>
    <main>
      <router-view />
    </main>
  </div>
</template>
<script>
import MetaMenu from "./components/shared/MetaMenu.vue";
import DebugToggle from "./components/shared/DebugToggle.vue";
import CommonHelper from "@/helpers/common.helper.js";
import moment from "moment";
import "@/modules/tooltip/module.main.js";

export default {
  components: { MetaMenu, DebugToggle },
  name: "App",
  data() {
    return {
      isLoggedIn: false,
      userName: "",
      fullName: "",
      enableMeetingRequests: false,
      enableFlightRequests: false,
      enableTrainRequests: false,
      enableCarRequests: false
    };
  },
  watch: {
    $route() {
      this.checkLoginStatus();
    }
  },
  async created() {
    moment.locale(this.languageIso);
    await this.checkLoginStatus();
    if (this.isLoggedIn) {
      this.injectCustomCss();
      let companyInfo = await CommonHelper.getCompanyInfo();
      this.fullName = companyInfo?.fullName ?? "";
      this.enableMeetingRequests = companyInfo?.enableMeetingRequests ?? true;
      this.enableFlightRequests = companyInfo?.enableFlightRequests ?? true;
      this.enableTrainRequests = companyInfo?.enableTrainRequests ?? true;
      this.enableCarRequests = companyInfo?.enableCarRequests ?? true;
    }
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  computed: {
    managementPortalUrl() {
      let companyInfo = JSON.parse(localStorage.companyInfo);
      return companyInfo?.managementPortalUrl
        ? companyInfo?.managementPortalUrl
        : null;
    }
  },
  methods: {
    handleResize() {
      if (window.innerWidth > 1023) {
        this.removeActive();
      }
    },

    onMetaMenuToggled(isOpen) {
      // If MetaMenu is opening, close the navbar
      if (isOpen) {
        const burger = document.querySelector(".navbar-burger");
        const navMenu = document.getElementById("l4bNavBar");

        if (burger) burger.classList.remove("is-active");
        if (navMenu) navMenu.classList.remove("is-active");
      }
    },
    removeActive() {
      const header = document.querySelector(".header");
      if (header) {
        const activeElements = header.querySelectorAll(".is-active");
        activeElements.forEach((el) => {
          el.classList.remove("is-active");
        });
      }
    },
    setLocale(locale) {
      localStorage.locale = locale;
      this.$i18n.locale = locale;

      setTimeout(() => window.location.reload(), 500);
    },
    async checkLoginStatus() {
      let userData = CommonHelper.isLoggedIn();
      if (userData) {
        this.isLoggedIn = true;
        this.userName = userData.userName;
        let companyInfo = await CommonHelper.getCompanyInfo();

        this.enableMeetingRequests = companyInfo.enableMeetingRequests ?? true;
        this.enableFlightRequests = companyInfo.enableFlightRequests ?? true;
        this.enableTrainRequests = companyInfo.enableTrainRequests ?? true;
        this.enableCarRequests = companyInfo?.enableCarRequests ?? true;
      } else {
        this.isLoggedIn = false;
      }
    }
  }
};
</script>

<style lang="scss">
.logo--big {
  position: absolute;
  right: 0;
  height: 60px;
  top: 50%;
  margin-top: -30px;
}
</style>
